'use client';

import Link from 'next/link';
import type { LucideIcon } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

export interface ButtonProps {
  active?: boolean;
  ariaLabel?: string;
  disabled?: boolean;
  href?: string;
  icon?: LucideIcon;
  onClick?: (
    e?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => void;
  role?: 'button' | 'link';
  style?: string;
  target?: string;
  title: string;
  type?: 'primary' | 'secondary';
}

const Button = ({
  active = false,
  ariaLabel,
  disabled = false,
  href,
  icon: Icon,
  onClick,
  role = 'button',
  style,
  target,
  title,
  type = 'primary',
}: ButtonProps) => {
  const btnStyles = `cursor-pointer text-center items-center inline-flex py-2 px-4 border-2 border-black text-sm font-bold rounded-[4px] hover:bg-blue focus-visible:bg-blue hover:text-black focus-visible:outline-none focus-visible:text-black`;
  const btnThemeStyles =
    type === 'primary' ? 'bg-white text-black' : 'bg-black text-white';
  const activeStyles = active ? 'bg-blue text-black' : '';

  const disabledStyles = disabled
    ? `opacity-50 cursor-not-allowed ${
        type === 'primary' ? 'hover:bg-white focus-visible:bg-white' : ''
      }`
    : '';

  const mergedStyles = twMerge(
    btnStyles,
    btnThemeStyles,
    activeStyles,
    disabledStyles,
    style
  );

  // A disabled button never renders as a link: a plain disabled <button> is
  // natively non-navigable (no href, so no middle-click either) and unfocusable.
  const asLink = !!href && !disabled;

  const content = (
    <>
      {Icon && (
        <Icon
          aria-hidden="true"
          className="mr-4 shrink-0"
          size={16}
          strokeWidth={1.75}
        />
      )}
      {title}
    </>
  );

  if (asLink && href.startsWith('http')) {
    return (
      <a
        aria-label={ariaLabel || title}
        {...(active ? { 'aria-current': active } : {})}
        href={href}
        className={mergedStyles}
        onClick={(e) => {
          if (onClick) {
            onClick(e);
          }
        }}
        role={role}
        target={target}
        rel={target === '_blank' ? 'noopener noreferrer' : undefined}
        title={ariaLabel || title}
      >
        {content}
      </a>
    );
  }

  return (
    <>
      {asLink ? (
        <Link
          aria-label={ariaLabel || title}
          aria-current={active}
          href={href}
          className={mergedStyles}
          onClick={(e) => {
            if (onClick) {
              onClick(e);
            }
          }}
          role={role}
          target={target}
          title={ariaLabel || title}
        >
          {content}
        </Link>
      ) : (
        <button
          aria-label={ariaLabel || title}
          aria-current={active}
          className={mergedStyles}
          disabled={disabled || undefined}
          onClick={(e) => {
            if (onClick) {
              onClick(e);
            }
          }}
          role={role}
          title={ariaLabel || title}
        >
          {content}
        </button>
      )}
    </>
  );
};

export default Button;
